import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  AlgunaCheckoutProps,
  AlgunaCheckoutRef,
  CheckoutState,
} from '@alguna/checkout-sdk/react';

import {
  useAlgunaCheckoutConfirmation,
  useAlgunaCheckoutSession,
} from './queries';
import {
  AlgunaCheckoutOptions,
  AlgunaCheckoutPlan,
  AlgunaCheckoutUnavailableReason,
} from './types';

import { monitoring } from '@/services/monitoring';

export type EmbedProps = {
  ref: RefObject<AlgunaCheckoutRef>;
  url: string;
  onCheckoutStateChange: (state: CheckoutState) => void;
  onRender: () => void;
  onComplete: () => void;
  onError: NonNullable<AlgunaCheckoutProps['onError']>;
};

type CheckoutActions = {
  canSubmit: boolean;
  isProcessing: boolean;
  submit: () => void;
};

type CheckoutOptions = {
  plan: AlgunaCheckoutPlan;
  /** Plan-specific setup options, sent with an add-on checkout */
  options?: AlgunaCheckoutOptions;
  paid: boolean;
  onPaid: () => void;
  onComplete: () => void;
};

export const PROCESSING_TIMEOUT_MS = 330_000; // 5.5 minutes

export type CheckoutActivity = 'idle' | 'loading' | 'confirming';

export type ReadyCheckout = {
  status: 'ready';
  embed: EmbedProps;
};

export type CheckoutStatus =
  | { status: 'loading' }
  | { status: 'active' }
  | { status: 'unavailable'; reason: AlgunaCheckoutUnavailableReason }
  | { status: 'paid'; confirmationExhausted: boolean }
  | { status: 'error'; retry: () => void; incomplete?: boolean }
  | ReadyCheckout;

export type AlgunaCheckoutController = CheckoutActions & {
  activity: CheckoutActivity;
  state: CheckoutStatus;
};

function activityFor(
  state: CheckoutStatus,
  painted: boolean,
  embedState: CheckoutState | undefined
): CheckoutActivity {
  switch (state.status) {
    case 'paid':
      return state.confirmationExhausted ? 'idle' : 'confirming';
    case 'loading':
      return 'loading';
    case 'ready':
      if (embedState?.isComplete) return 'confirming';
      return painted ? 'idle' : 'loading';
    default:
      return 'idle';
  }
}

function getFailureReason({
  isError,
  embedFailed,
  timedOut,
  hasUrl,
}: {
  isError: boolean;
  embedFailed: boolean;
  timedOut: boolean;
  hasUrl: boolean;
}) {
  if (isError) return 'session_error';
  if (embedFailed) return 'embed_error';
  if (timedOut) return 'processing_timeout';
  if (!hasUrl) return 'missing_url';
}

export function useAlgunaCheckout(
  teamUuid: string,
  { plan, options, paid, onPaid, onComplete: onCompleteProp }: CheckoutOptions
): AlgunaCheckoutController {
  const embedRef = useRef<AlgunaCheckoutRef>(null);
  const [checkoutState, setCheckoutState] = useState<CheckoutState>();
  const [embedFailed, setEmbedFailed] = useState(false);
  const [painted, setPainted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const onComplete = useCallback(() => {
    monitoring.trackAction('checkout_completed', { teamUuid });
    onCompleteProp();
  }, [teamUuid, onCompleteProp]);

  const { data, isLoading, isError, refetch } = useAlgunaCheckoutSession(
    teamUuid,
    plan,
    options,
    onComplete
  );

  const session = data?.status === 'checkout' ? data : undefined;

  const { exhausted: confirmationExhausted } = useAlgunaCheckoutConfirmation({
    teamUuid,
    sessionId: session?.sessionId,
    enabled: paid,
    onConfirmed: onComplete,
  });

  const submit = useCallback(() => {
    monitoring.trackAction('checkout_submit', { teamUuid });
    embedRef.current?.submit();
  }, [teamUuid]);

  const retry = useCallback(() => {
    setEmbedFailed(false);
    setPainted(false);
    setTimedOut(false);
    setCheckoutState(undefined);
    void refetch();
  }, [refetch]);

  const onRender = useCallback(() => setPainted(true), []);

  useEffect(() => {
    if (!checkoutState?.isProcessing || checkoutState.isComplete) return;
    if (paid || timedOut) return;
    const timer = setTimeout(() => setTimedOut(true), PROCESSING_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [checkoutState?.isProcessing, checkoutState?.isComplete, paid, timedOut]);

  // States that don't show the embed, so there's no checkout to fail
  function resolveStateWithoutEmbed(): CheckoutStatus | undefined {
    if (paid) return { status: 'paid', confirmationExhausted };
    if (isLoading) return { status: 'loading' };
    if (data?.status === 'active') return { status: 'active' };
    if (data?.status === 'unavailable') {
      return { status: 'unavailable', reason: data.reason };
    }
  }

  const stateWithoutEmbed = resolveStateWithoutEmbed();
  const failureReason = stateWithoutEmbed
    ? undefined
    : getFailureReason({
        isError,
        embedFailed,
        timedOut,
        hasUrl: Boolean(session?.url),
      });

  useEffect(() => {
    if (failureReason) {
      monitoring.trackAction('checkout_failed', {
        teamUuid,
        reason: failureReason,
      });
    }
  }, [failureReason, teamUuid]);

  const onEmbedError = useCallback<EmbedProps['onError']>(
    (event) => {
      if (event.code === 'session_expired') {
        void refetch();
        return;
      }
      setEmbedFailed(true);
    },
    [refetch]
  );

  const actions = {
    canSubmit: Boolean(checkoutState?.canSubmit) && !paid && !timedOut,
    isProcessing: (Boolean(checkoutState?.isProcessing) && !timedOut) || paid,
    submit,
  };

  function resolveState(): CheckoutStatus {
    if (stateWithoutEmbed) return stateWithoutEmbed;
    if (failureReason || !session?.url) {
      return { status: 'error', retry, incomplete: timedOut };
    }

    return {
      status: 'ready',
      embed: {
        ref: embedRef,
        url: session.url,
        onCheckoutStateChange: setCheckoutState,
        onRender,
        onComplete: onPaid,
        onError: onEmbedError,
      },
    };
  }

  const state = resolveState();

  return {
    ...actions,
    activity: activityFor(state, painted, checkoutState),
    state,
  };
}
